import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, CheckCircle, Lock, AlertCircle, ShieldCheck } from 'lucide-react';
import { useMe, useUpdateMe, useBrokerCurrent } from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { SectionHeader, Spinner } from '../../components/ui';
import { toast } from '../../store';
import { ROUTES } from '../../routes/constants';

function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    super_admin: 'bg-amber/20 text-amber border-amber/40',
    admin:       'bg-warn/20 text-warn border-warn/40',
    operator:    'bg-info/20 text-info border-info/40',
    end_user:    'bg-bg-surface text-txt-muted border-bg-border',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-2xs font-mono ${colors[role] ?? colors.end_user}`}>
      {role === 'end_user' ? 'USER' : role}
    </span>
  );
}

export default function ProfilePage() {
  const { data: profile, isLoading, error: loadError } = useMe();
  const { data: broker } = useBrokerCurrent();
  const updateMe = useUpdateMe();
  const setSession = useSessionStore((s) => s.setSession);
  const token     = useSessionStore((s) => s.token);

  const brokerLocked = !!(broker && broker.is_active);
  // Taken from the server-loaded account, never the client session role: the
  // backend rejects any self identity write from an account holding Operator.
  const isOperatorAccount = Array.isArray(profile?.roles) && profile.roles.includes('operator');

  const [form, setForm] = useState({ full_name: '', email: '', phone: '' });
  const [editing, setEditing]   = useState(false);
  const [saveError, setSaveError] = useState('');
  const isEditing = editing && !isOperatorAccount;

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        email:     profile.email     || '',
        phone:     profile.phone     || '',
      });
    }
  }, [profile]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isOperatorAccount) return;
    setSaveError('');
    const payload: Record<string, string> = { full_name: form.full_name };
    if (!brokerLocked) {
      if (form.email !== profile?.email) payload.email = form.email;
      if (form.phone !== profile?.phone) payload.phone = form.phone;
    }
    try {
      const updated = await updateMe.mutateAsync(payload);
      // Keep session user in sync
      if (token) {
        setSession({ user: updated, token, roles: updated.roles });
      }
      toast.success('Profile updated');
      setEditing(false);
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to save profile.');
    }
  };

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>;

  if (loadError) {
    return (
      <div className="max-w-lg">
        <div className="panel p-6 flex items-center gap-3 text-bear">
          <AlertCircle size={18} />
          <p className="text-sm">Failed to load profile. Please refresh.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-5">
      <SectionHeader title="Profile" subtitle="Your account information and settings" />

      {isOperatorAccount && (
        <div className="panel px-4 py-3 flex items-start gap-3 text-xs text-txt-secondary" data-testid="operator-identity-notice">
          <Lock size={14} className="text-warn mt-0.5 shrink-0" />
          <span>
            Operator account details (full name, email and phone) are managed by the Super Admin.
            You can still <Link to={ROUTES.CHANGE_PASSWORD} className="text-amber hover:underline">change your password</Link>.
          </span>
        </div>
      )}

      {/* Identity lock notice */}
      {brokerLocked && !isOperatorAccount && (
        <div className="panel px-4 py-3 flex items-center gap-3 text-warn text-xs">
          <Lock size={14} />
          <span>Your email and phone are identity-locked while a broker is mapped. Visit <strong>Broker</strong> to unmap.</span>
        </div>
      )}

      <div className="panel p-6 space-y-5">
        {/* Profile header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-amber/20 flex items-center justify-center">
            <User size={22} className="text-amber" />
          </div>
          <div>
            <p className="font-display font-semibold text-txt-primary">{profile?.full_name || profile?.username}</p>
            <p className="text-xs font-mono text-txt-muted">@{profile?.username}</p>
          </div>
          <div className="ml-auto flex gap-1.5 flex-wrap justify-end">
            {(profile?.roles || []).map((r: string) => <RoleBadge key={r} role={r} />)}
          </div>
        </div>

        {/* Status row */}
        <div className="flex items-center gap-4 text-xs text-txt-secondary">
          <span>Status: <span className={`font-semibold ${profile?.status === 'active' ? 'text-bull' : 'text-bear'}`}>{profile?.status ?? '—'}</span></span>
          <span className="flex items-center gap-1">
            {profile?.email_verified
              ? <><CheckCircle size={12} className="text-bull" /> Email verified</>
              : <><AlertCircle size={12} className="text-warn" /> Email not verified</>}
          </span>
        </div>

        {/* Edit form */}
        <form onSubmit={handleSave} className="space-y-4 pt-2 border-t border-bg-border">
          <div className="space-y-1">
            <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Full Name</label>
            <input
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              className="input-base"
              disabled={!isEditing}
              readOnly={isOperatorAccount}
            />
          </div>

          <div className="space-y-1">
            <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest flex items-center gap-1.5">
              Email {brokerLocked && <Lock size={11} className="text-warn" />}
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="input-base"
              disabled={!isEditing || brokerLocked}
              readOnly={isOperatorAccount}
            />
          </div>

          <div className="space-y-1">
            <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest flex items-center gap-1.5">
              Phone {brokerLocked && <Lock size={11} className="text-warn" />}
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className="input-base"
              disabled={!isEditing || brokerLocked}
              readOnly={isOperatorAccount}
            />
          </div>

          {saveError && !isOperatorAccount && (
            <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded px-3 py-2">{saveError}</p>
          )}

          {!isOperatorAccount && <div className="flex gap-3 justify-end pt-1">
            {isEditing ? (
              <>
                <button type="button" onClick={() => { setEditing(false); setSaveError(''); }} className="btn-ghost">
                  Cancel
                </button>
                <button type="submit" disabled={updateMe.isPending} className="btn-primary">
                  {updateMe.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button type="button" onClick={() => setEditing(true)} className="btn-primary">
                Edit Profile
              </button>
            )}
          </div>}
        </form>

        {/* Read-only info */}
        <div className="pt-2 border-t border-bg-border text-xs">
          <div>
            <p className="text-txt-muted font-mono uppercase tracking-widest text-2xs">Joined</p>
            <p className="font-mono text-txt-secondary mt-0.5">
              {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Admin Access Toggle */}
      <div className="panel p-6 space-y-3">
        <div className="flex items-center gap-3">
          <ShieldCheck size={18} className="text-amber" />
          <div className="flex-1">
            <p className="text-sm font-medium text-txt-primary">Allow Admin Access</p>
            <p className="text-xs text-txt-muted mt-0.5">
              When enabled, your operator or admin can view and manage your strategies and broker settings.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!profile?.allow_admin_access}
            onClick={async () => {
              try {
                await updateMe.mutateAsync({ allow_admin_access: !profile?.allow_admin_access });
                toast.success(profile?.allow_admin_access ? 'Admin access disabled' : 'Admin access enabled');
              } catch {
                toast.error('Failed to update setting');
              }
            }}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
              profile?.allow_admin_access ? 'bg-bull' : 'bg-bg-hover'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg transition-transform duration-200 ${
                profile?.allow_admin_access ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
